import "./EditFile.scss";

import { BackgroundJobsDrawer } from "components/BackgroundJobsDrawer/BackgroundJobsDrawer";
import { KaboodleToaster } from "components/common";
import Documents from "components/Documents/drawer/DocumentsDrawer";
import TaskModal from "pages/tasks/components/modals/TaskModal";
import UploadClaimsOnFileModal from "components/Modals/UploadClaimsOnFileModal";
import Notes from "components/Notes";
import { fileToNotesEntity } from "components/Notes/notesAdapter";
import { QuerySection } from "components/QuerySection/QuerySection";
import type { Tab } from "components/Tabs/Tabs";
import Tabs from "components/Tabs/Tabs";
import ClientInformationDrawer from "pages/client/ClientInformationDrawer/ClientInformationDrawer";
import ClaimLinkingDrawerTable from "pages/files/ClaimLinkingDrawerTable";
import RelatedFilesDrawerTable from "pages/files/RelatedFilesDrawerTable";
import TaskDrawerTable from "pages/tasks/components/board/TaskDrawerTable";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  clearCurrentTask,
  setCurrentFile,
  setShowArchiveModal,
  setShowMergeModal,
} from "reducers/files";
import { setService } from "reducers/future-medical";
import { setLiens } from "reducers/liens";
import {
  setIsLoading,
  setShowTaskModal,
  setShowUploadClaimsOnFileModal,
  setValidationState,
} from "reducers/main";
import {
  useGetFileByIdQuery,
  useGetFileValidationByIdQuery,
  usePatchFileByIdMutation,
  usePatchFileStatusByIdMutation,
} from "services/file-api";
import { useDispatch, useSelector } from "utils/redux";

import { RRESelection } from "./EditRRE";
import EmailCorrespondence from "./EmailCorrespondence";
import FileAuditHistory from "./FileAuditHistory";
import FilePageContainer from "./FilePageContainer";
import AccidentInformationForm from "./sections/AccidentInformationForm";
import AdjusterInformationForm from "./sections/AdjusterInformation";
import ClaimantInformationForm from "./sections/ClaimantInformationForm";
import ClientAndEmployerInformation from "./sections/ClientAndEmployerInformation";
import EditFileOwner from "./sections/EditFileOwner";
import EditServiceAssignees from "./sections/EditServiceAssignees";
import EditServiceForm from "./sections/EditServiceForm";
import InsurerInformationForm from "./sections/InsurerInformation";
import LinkedClaimList from "./sections/LinkedClaimList";
import ReferringPartyInformationForm from "./sections/ReferringPartyInformation";
import TPAInformationForm from "./sections/TPAInformation";
import TeamMemberBandwidth from "./TeamMemberBandwidth";

const EditFile = () => {
  const dispatch = useDispatch();
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [showRRESelection, setShowRRESelection] = useState(false);
  const [patchFileStatus] = usePatchFileStatusByIdMutation();

  // extract tab from URL path or default to claimant-information
  const getActiveTabFromUrl = useCallback(() => {
    const pathSegments = location.pathname.split("/");
    const tabFromUrl = pathSegments[pathSegments.length - 1];
    const validTabs = [
      "claimant-information",
      "service-selection",
      "client-parties",
      "query",
      "linked-claims",
      "audit-history",
      "notes",
    ];
    return validTabs.includes(tabFromUrl) ? tabFromUrl : "claimant-information";
  }, [location.pathname]);

  const [activeTab, setActiveTab] = useState(getActiveTabFromUrl);

  // update active tab when URL changes
  useEffect(() => {
    setActiveTab(getActiveTabFromUrl());
  }, [getActiveTabFromUrl]);

  const showTaskModal = useSelector((state) => state.main.showTaskModal);
  const showUploadClaimsOnFileModal = useSelector(
    (state) => state.main.showUploadClaimsOnFileModal,
  );
  const loggedInUser = useSelector((state) => state.main.loggedInUser);

  // determine if components should be read-only based on operations authority
  const isReadOnly = !loggedInUser?.has_operations_authority;

  const { data: fileData, isLoading } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );

  useEffect(() => {
    dispatch(setCurrentFile(fileData));
  }, [fileData, dispatch]);

  // clear stale service state when entering file view
  useEffect(() => {
    dispatch(setService(null));
    dispatch(setLiens([]));
  }, [dispatch]);

  const { data: fileValidation } = useGetFileValidationByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (fileValidation) {
      const validationData = Object.values(fileValidation).flat();
      const errors = validationData.filter((item) => item.level === "ERROR");
      const warnings = validationData.filter((item) => item.level === "WARN");

      dispatch(setValidationState({ errors, warnings }));
    }
  }, [dispatch, fileValidation]);

  const handleClose = useCallback(() => {
    dispatch(clearCurrentTask());
    dispatch(setShowTaskModal(false));
  }, [dispatch]);

  const handleStatusTransition = useCallback(async () => {
    try {
      let targetStatus;
      let successMessage;

      if (!fileData?.status || fileData.status === "U") {
        targetStatus = "O";
        successMessage = "File opened successfully";
      } else if (fileData.status === "O") {
        targetStatus = "C";
        successMessage = "File marked as completed";
      }

      await patchFileStatus({
        id: fileId,
        status: targetStatus,
      }).unwrap();

      KaboodleToaster.success(successMessage);
    } catch (err) {
      const errorMessage =
        err?.data?.errors?.map((error) => error.message).join(", ") ||
        "An unexpected error occurred";

      KaboodleToaster.error(`Failed to update file status: ${errorMessage}`);
    }
  }, [fileData, patchFileStatus]);

  const handleArchiveFile = () => {
    dispatch(setShowArchiveModal(true));
  };

  const handleMergeFile = () => {
    dispatch(setShowMergeModal(true));
  };

  // merging is only offered before a file is opened -- once work has started
  // there is nothing sensible to fold into another file. Requires the file
  // to have loaded so the button doesn't flash on every file while fetching.
  const isUnprocessed =
    !!fileData && (!fileData.status || fileData.status === "U");

  const handleTabChange = useCallback(
    (tabId: string) => {
      // navigate to the new tab URL
      navigate(`/operations/files/${fileId}/edit/${tabId}`, { replace: true });
    },
    [navigate, fileId],
  );

  const taskModal = useMemo(() => {
    return (
      <TaskModal isOpen={showTaskModal} onClose={handleClose} fileId={fileId} />
    );
  }, [showTaskModal, handleClose, fileId]);

  const uploadClaimsModal = useMemo(() => {
    return (
      <UploadClaimsOnFileModal
        isOpen={showUploadClaimsOnFileModal}
        onClose={() => dispatch(setShowUploadClaimsOnFileModal(false))}
      />
    );
  }, [showUploadClaimsOnFileModal, dispatch]);

  const getPrimaryButtonText = useCallback(() => {
    if (!fileData?.status || fileData.status === "U") {
      return "OPEN FILE";
    } else {
      return "COMPLETE FILE";
    }
  }, [fileData]);

  // get priority display info
  const getPriorityInfo = useCallback(() => {
    if (!fileData) return { icon: null, label: "Normal", className: "" };

    if (fileData.is_24_hour_rush) {
      return {
        icon: <span className="material-icons">circle</span>,
        label: "24h Rush",
        className: "priority-urgent",
      };
    }
    if (fileData.is_3_day_rush) {
      return {
        icon: <span className="material-icons">circle</span>,
        label: "3 Day Rush",
        className: "priority-high",
      };
    }
    if (fileData.is_rush) {
      return {
        icon: <span className="material-icons">circle</span>,
        label: "Rush",
        className: "priority-medium",
      };
    }
    return {
      icon: null,
      label: "Normal",
      className: "priority-normal",
    };
  }, [fileData]);

  const priorityInfo = getPriorityInfo();

  const statusControlBar = useMemo(() => {
    return (
      <>
        <div className="footer-spacer"></div>
        <footer className="status-control-bar">
          <div className="footer-left-group">
            <div className="status-section">
              <div className="status-section__label">File Priority</div>
              <div
                className={`status-section__content priority-badge ${priorityInfo.className}`}
              >
                {priorityInfo.icon}
                <span className="priority-badge__label">
                  {priorityInfo.label}
                </span>
              </div>
            </div>

            <div className="status-divider"></div>

            <div className="status-section">
              <div className="status-section__label">Due Date</div>
              <div className="status-section__content">
                <span className="due-date-badge">
                  {fileData?.due_date || "Not set"}
                </span>
              </div>
            </div>
          </div>

          <div className="status-section status-section--actions">
            <div className="status-section__label">File Status</div>
            <div className="status-section__content">
              <span
                className={`status-badge ${
                  fileData?.status === "U" || !fileData?.status
                    ? "unprocessed"
                    : fileData?.status === "O"
                      ? "opened"
                      : fileData?.status === "C"
                        ? "complete"
                        : ""
                }`}
              >
                {fileData?.status_verbose || "Unprocessed"}
              </span>
              <div className="status-content-divider"></div>
              <div className="button-group">
                <button
                  className="button add"
                  onClick={handleStatusTransition}
                  disabled={fileData?.status === "C"}
                >
                  {getPrimaryButtonText()}
                </button>
                <button className="button" onClick={handleArchiveFile}>
                  ARCHIVE FILE
                </button>
                {isUnprocessed && (
                  <button
                    className="button"
                    onClick={handleMergeFile}
                    data-testid="merge-file-button"
                  >
                    MERGE FILE
                  </button>
                )}
              </div>
            </div>
          </div>
        </footer>
      </>
    );
  }, [
    fileData,
    handleStatusTransition,
    handleArchiveFile,
    handleMergeFile,
    isUnprocessed,
    getPrimaryButtonText,
    priorityInfo,
  ]);

  const ClaimLinking = useMemo(() => {
    return fileData?.has_section_111_claim_linkage_available ? (
      <LinkedClaimList fileData={fileData} isReadOnly={isReadOnly} />
    ) : null;
  }, [fileData, isReadOnly]);

  const [patchFileById] = usePatchFileByIdMutation();

  const handleCoverageChange = useCallback(
    async (field: "coverage_complete" | "no_coverage", value: boolean) => {
      if (!fileData || !fileId) return;

      try {
        await patchFileById({
          id: fileId,
          data: { [field]: value },
        }).unwrap();

        const message =
          field === "no_coverage"
            ? value
              ? "Marked as No Coverage"
              : "No Coverage status removed"
            : value
              ? "Coverage marked as complete"
              : "Coverage marked as incomplete";

        KaboodleToaster.success(message);
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
        KaboodleToaster.error(`Failed to update ${field}`);
      }
    },
    [fileData, fileId, patchFileById],
  );

  const tabs: Tab[] = useMemo(
    () => [
      {
        id: "claimant-information",
        label: (
          <>
            <span className="material-icons">person</span>
            Claimant Information
          </>
        ),
        content: (
          <div className="grid-layout gl-2">
            <ClaimantInformationForm isReadOnly={isReadOnly} />
            <AccidentInformationForm
              fileData={fileData}
              isReadOnly={isReadOnly}
            />
          </div>
        ),
      },
      {
        id: "service-selection",
        label: (
          <>
            <span className="material-icons">work</span>
            Service Selection
          </>
        ),
        content: (
          <div className="tab-content-wrapper">
            <EmailCorrespondence />
            <EditServiceForm
              setShowRRESelection={setShowRRESelection}
              isReadOnly={isReadOnly}
            />
            <EditServiceAssignees
              fileServices={fileData?.services}
              fileId={fileData?.id}
              isReadOnly={isReadOnly}
            />
            <div className="grid-layout gl-2">
              <EditFileOwner isReadOnly={isReadOnly} />
              <TeamMemberBandwidth />
            </div>
            {showRRESelection && <RRESelection isReadOnly={isReadOnly} />}
          </div>
        ),
      },
      {
        id: "client-parties",
        label: (
          <>
            <span className="material-icons">business</span>
            Client & Parties
          </>
        ),
        content: (
          <div className="tab-content-wrapper">
            <ClientAndEmployerInformation isReadOnly={isReadOnly} />
            <ReferringPartyInformationForm isReadOnly={isReadOnly} />
            <AdjusterInformationForm isReadOnly={isReadOnly} />
            <TPAInformationForm isReadOnly={isReadOnly} />
            <InsurerInformationForm isReadOnly={isReadOnly} />
          </div>
        ),
      },
      {
        id: "query",
        label: (
          <>
            <span className="material-icons">search</span>
            Query
          </>
        ),
        content: (
          <div className="tab-content-wrapper query-tab-content">
            {fileData && (
              <QuerySection
                fileId={fileData.id}
                organization={fileData.organization}
                isCoverageComplete={fileData.coverage_complete}
                isNoCoverage={fileData.no_coverage}
                onCoverageChange={handleCoverageChange}
              />
            )}
          </div>
        ),
      },
      {
        id: "linked-claims",
        label: (
          <>
            <span className="material-icons">link</span>
            Linked Claims
          </>
        ),
        content: (
          <div className="tab-content-wrapper">
            {ClaimLinking || (
              <div className="no-claims-message">
                <p>No Section 111 claim linkage available for this file.</p>
              </div>
            )}
          </div>
        ),
      },
      {
        id: "audit-history",
        label: (
          <>
            <span className="material-icons">history</span>
            Audit History
          </>
        ),
        content: (
          <div className="tab-content-wrapper">
            <FileAuditHistory />
          </div>
        ),
      },
      {
        id: "notes",
        label: (
          <>
            <span className="material-icons">note</span>
            Notes
          </>
        ),
        content: (
          <div className="tab-content-wrapper">
            {fileData && <Notes entity={fileToNotesEntity(fileData)} />}
          </div>
        ),
      },
    ],
    [fileData, showRRESelection, ClaimLinking, isReadOnly],
  );

  return (
    <FilePageContainer>
      {taskModal}
      {showRRESelection ? uploadClaimsModal : null}
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        className="edit-file-tabs"
      />
      <Documents fileId={Number(fileId)} />
      <TaskDrawerTable fileId={fileId} />
      {!isReadOnly && statusControlBar}
      <ClaimLinkingDrawerTable fileData={fileData} />
      <RelatedFilesDrawerTable fileData={fileData} />
      <ClientInformationDrawer service={fileData} />
      {fileId && <BackgroundJobsDrawer filters={{ file_id: fileId }} />}
    </FilePageContainer>
  );
};

export default EditFile;
